<script setup>
import { computed } from 'vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';

const props = defineProps({
  data: {
    type: Object,
    required: true,
  },
});

const velocity = computed(() => props.data?.velocity || 0);
</script>

<template>
  <div class="rounded-lg border border-n-weak bg-n-solid-2 p-5">
    <!-- Header -->
    <div class="mb-4 flex items-center justify-between">
      <h3 class="text-sm font-medium text-n-slate-12">
        {{ $t('PIPELINE.ANALYTICS.SALES_VELOCITY.TITLE') }}
      </h3>
      <Icon icon="i-lucide-trending-up" class="size-5 text-n-slate-9" />
    </div>

    <!-- Main Metric -->
    <div class="mb-5">
      <div
        class="inline-flex items-baseline gap-2 rounded-lg border border-n-blue-6 bg-n-blue-3 px-3 py-1.5"
      >
        <span class="text-3xl font-semibold text-n-blue-11">{{
          velocity.toFixed(2)
        }}</span>
        <span class="text-xs font-medium text-n-blue-11">{{
          $t('PIPELINE.ANALYTICS.SALES_VELOCITY.VELOCITY')
        }}</span>
      </div>
    </div>

    <!-- Components Grid -->
    <div class="grid grid-cols-2 gap-3">
      <div class="rounded-lg bg-n-alpha-2 p-3">
        <div class="text-xs text-n-slate-11">
          {{ $t('PIPELINE.ANALYTICS.SALES_VELOCITY.OPPORTUNITIES') }}
        </div>
        <div class="text-xl font-semibold text-n-slate-12">
          {{ data?.total_opportunities || 0 }}
        </div>
      </div>

      <div class="rounded-lg bg-n-teal-3 p-3">
        <div class="text-xs text-n-slate-11">
          {{ $t('PIPELINE.ANALYTICS.SALES_VELOCITY.AVG_DEAL_VALUE') }}
        </div>
        <div class="text-xl font-semibold text-n-teal-11">
          {{ $t('PIPELINE.ANALYTICS.SALES_VELOCITY.CURRENCY')
          }}{{ (data?.average_deal_size || 0).toFixed(2) }}
        </div>
      </div>

      <div class="rounded-lg bg-n-blue-3 p-3">
        <div class="text-xs text-n-slate-11">
          {{ $t('PIPELINE.ANALYTICS.WIN_RATE.PERCENTAGE') }}
        </div>
        <div class="text-xl font-semibold text-n-blue-11">
          {{ (data?.win_rate || 0).toFixed(1) }}%
        </div>
      </div>

      <div class="rounded-lg bg-n-amber-3 p-3">
        <div class="text-xs text-n-slate-11">
          {{ $t('PIPELINE.ANALYTICS.SALES_VELOCITY.AVG_CYCLE') }}
        </div>
        <div class="text-xl font-semibold text-n-amber-11">
          {{ (data?.average_sales_cycle || 0).toFixed(1) }}
          <span class="text-xs">{{
            $t('PIPELINE.ANALYTICS.SALES_VELOCITY.DAYS')
          }}</span>
        </div>
      </div>
    </div>

    <!-- By Pipeline (if available) -->
    <div
      v-if="data?.by_pipeline && data.by_pipeline.length > 0"
      class="mt-5 border-t border-n-weak pt-4"
    >
      <h4 class="mb-3 text-xs font-medium text-n-slate-11">
        {{ $t('PIPELINE.ANALYTICS.SALES_VELOCITY.BY_PIPELINE') }}
      </h4>
      <div class="space-y-2">
        <div
          v-for="pipeline in data.by_pipeline"
          :key="pipeline.pipeline_id"
          class="flex items-center justify-between rounded-lg bg-n-alpha-2 px-3 py-2"
        >
          <span class="text-sm text-n-slate-11">{{
            pipeline.pipeline_name
          }}</span>
          <span class="text-sm font-medium text-n-slate-12">
            {{ pipeline.velocity.toFixed(2) }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>
